import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { workspace } from "@/components/workspace/controller";
import { noteHref } from "@/lib/url-state";
import { cn } from "@/lib/utils";
import type { NoteCardOut } from "@/lib/api";
import { useShell } from "@/stores/shell";

import { FolderBreadcrumb } from "./FolderBreadcrumb";
import { NotePreview, isLightSurface } from "./NotePreview";

/**
 * A note card — the "Leaf" design: preview-first, bleeding to the card's edges,
 * with the folder path laid over its top edge inside a scrim, then title,
 * description and a small number of tags below.
 *
 * Chosen because this vault is visually varied (handwriting, PDFs, web
 * snapshots), where a page image identifies a note faster than its title does.
 * The cost is that the preview is LOAD-BEARING rather than decorative, which is
 * why `NotePreview` degrades through raster → text excerpt → branded card →
 * tinted field rather than ever rendering an empty frame.
 *
 * DOM order is path → title → description → tags (the preview image is
 * `aria-hidden`), so the reading order for assistive tech matches the brief's
 * top-to-bottom list even though the path is drawn on the preview.
 *
 * It is a real `<a href>`, not a div with a click handler: middle-click,
 * ⌘/Ctrl-click, "Open link in new tab" and "Copy link address" then all work
 * the way they do everywhere else on the web. A plain click is intercepted and
 * opens a Doc View tab in the workspace dock instead — instant, no app boot.
 */
export function NoteCard({
  card,
  onSelectFolder,
}: {
  card: NoteCardOut;
  onSelectFolder?: (path: string) => void;
}) {
  const processing = card.status === "processing" || card.status === "registered";
  const failed = card.status === "failed";
  const { mode, url, surface } = card.preview;
  // Two different problems, two different answers.
  //
  // A PLATE is a page we painted, so we know exactly how light it is: print the
  // path on it in dark ink, the way it would be printed on paper. Darkening a
  // clean sheet with a scrim just to fit light text turns it into a smudged one.
  //
  // A RASTER is a page we did not paint and cannot inspect — a scan is usually
  // white but might be anything — so it keeps the scrim, which is legible over
  // either.
  const onLightPlate = mode === "card" && isLightSurface(surface?.background);
  const needsScrim = mode === "image" && !!url;

  /** `background` is the ⌘/Ctrl-click contract: the note opens as a tab you can
   * get to later, and you stay on the page you were reading. */
  const open = (background = false) => {
    if (!background) useShell.getState().closeAll();
    if (card.kind === "node") {
      workspace.openMarkdown(card.id, card.title, { background });
      return;
    }
    if (failed && !background) {
      toast.error("This document didn't finish processing", {
        description: "Open it to see the error, or re-upload it.",
      });
    }
    workspace.openDoc(
      {
        id: card.id,
        title: card.title,
        docKind: "document",
        nodeType: card.node_type,
      },
      { background },
    );
  };

  return (
    // The breadcrumb is a SIBLING of the anchor, not a child of it: its segments
    // are their own controls (they narrow the feed), and interactive content
    // nested inside an <a> is invalid HTML and ambiguous for assistive tech. It
    // is laid over the preview absolutely, so it still reads as part of it.
    <div
      className={cn(
        // The app's Card surface: borderless, --radius rounding, the same soft
        // shadow. The hover lift is the only thing added, and it is what makes
        // a grid of cards read as clickable without borders.
        "group relative flex flex-col overflow-hidden rounded-lg bg-card",
        "shadow-[0_16px_50px_-20px_rgba(0,0,0,0.85)] transition-[transform,box-shadow] duration-200",
        "hover:-translate-y-0.5 hover:shadow-[0_22px_60px_-20px_rgba(0,0,0,0.9)]",
        "focus-within:ring-2 focus-within:ring-ring",
      )}
    >
      <a
        href={noteHref(card.id, card.kind)}
        onClick={(e) => {
          if (e.button !== 0) return;
          // ⌘/Ctrl-click means "open in a tab, don't take me there" — and the
          // tab it belongs in is THIS app's dock, not a second copy of the whole
          // studio in a browser tab. Shift/Alt still fall through to the browser
          // (new window, download) where the native behaviour is the right one.
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            open(true);
            return;
          }
          if (e.shiftKey || e.altKey) return;
          e.preventDefault();
          open();
        }}
        // Middle-click is the other "open in a background tab" gesture, and it
        // never fires `click` — the browser fires `auxclick` instead.
        onAuxClick={(e) => {
          if (e.button !== 1) return;
          e.preventDefault();
          open(true);
        }}
        className="flex flex-1 flex-col text-left no-underline focus:outline-none"
      >
        {/* Preview, bleeding to the card edges. */}
        <div className="relative h-[124px] w-full shrink-0">
          {/* aria-hidden: the excerpt is a VISUAL cue. Left announceable it was
              read out in full before the title, burying the one thing that
              identifies the note. */}
          <div aria-hidden className="h-full w-full">
            <NotePreview card={card} />
          </div>
          {/* A scrim wherever the preview is LIGHT — a captured raster, or a
              page painted in its own white/cream paper — because the breadcrumb
              is muted-foreground text and would otherwise vanish into it. Over
              the dark text previews it would be a visible band across the top of
              every card for no reason, so it is not drawn there. */}
          {needsScrim && (
            <div className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-black/70 via-black/35 to-transparent" />
          )}
          {(processing || failed) && (
            <span
              className={cn(
                "absolute bottom-2 right-2 flex items-center gap-1 rounded-full px-2 py-0.5",
                "font-mono text-[9px] uppercase tracking-widest backdrop-blur-sm",
                failed
                  ? "bg-destructive/20 text-destructive"
                  : "bg-background/70 text-muted-foreground",
              )}
            >
              {failed ? (
                <AlertTriangle className="h-2.5 w-2.5" />
              ) : (
                <Loader2 className="h-2.5 w-2.5 animate-spin" />
              )}
              {failed ? "failed" : "processing"}
            </span>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1.5 px-3 pb-3 pt-2.5">
          <h3 className="line-clamp-2 font-display text-[15px] font-medium leading-[1.25] text-foreground">
            {card.title}
          </h3>
          {card.description ? (
            <p className="line-clamp-2 text-[11.5px] leading-[1.5] text-muted-foreground">
              {card.description}
            </p>
          ) : (
            // The honest state for every markdown note today: enrichment doesn't
            // run on them yet. Saying so beats a blank line that reads as a
            // rendering bug — and it disappears on its own once W9 lands.
            <p className="text-[11.5px] italic leading-[1.5] text-muted-foreground/55">
              No description yet
            </p>
          )}
          {card.tags.length > 0 && (
            <div className="mt-auto flex flex-wrap gap-1 pt-1">
              {card.tags.map((tag) => (
                <span
                  key={tag}
                  className="max-w-full truncate rounded-full border border-border px-1.5 py-px font-mono text-[9.5px] text-muted-foreground"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </a>

      <FolderBreadcrumb
        card={card}
        onSelect={onSelectFolder}
        tone={onLightPlate ? "light" : "dark"}
        className="absolute inset-x-0 top-0 px-3 pt-2.5"
      />
    </div>
  );
}
